import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, X as XIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLocale } from "@/contexts/LocaleContext";
import type { TranslationKey } from "@/lib/translations";
import bildmarke from "@/assets/icons/Bildmarke_Menue.svg";
import phoneIcon from "@/assets/icons/Phone_Menue.svg";
import mailIcon from "@/assets/icons/Mail_Menue.svg";
import dropdownArrow from "@/assets/icons/Dropdown_arrow.svg";

const ANTHRAZIT = "text-[#383637]";
const DOT = "bg-[#A59FA1]";

interface SubEntry {
  labelKey: TranslationKey;
  to: string;
}
interface NavEntry {
  labelKey: TranslationKey;
  to: string;
  children?: SubEntry[];
}

const LEFT_NAV: NavEntry[] = [
  {
    labelKey: "nav.suites",
    to: "/suites",
    children: [
      { labelKey: "nav.suites_overview", to: "/suites#top" },
      { labelKey: "nav.suites_ausstattung", to: "/suites#ausstattung" },
      { labelKey: "nav.suites_buchung", to: "/suites#buchungsinformationen" },
    ],
  },
  { labelKey: "nav.lerch_plus", to: "/lerch-plus" },
];
const RIGHT_NAV: NavEntry[] = [
  { labelKey: "nav.wellness", to: "/wellness" },
  {
    labelKey: "nav.erlebnisse",
    to: "/erlebnisse",
    children: [
      { labelKey: "nav.erlebnisse_paznaun", to: "/erlebnisse" },
      { labelKey: "nav.sommer", to: "/erlebnisse/sommer" },
      { labelKey: "nav.winter", to: "/erlebnisse/winter" },
      { labelKey: "nav.events", to: "/erlebnisse/top-events" },
    ],
  },
];

const navLinkClass =
  `${ANTHRAZIT} font-display uppercase tracking-[0.15em] ` +
  `text-[13px] xl:text-[15px] 2xl:text-[17px] ` +
  `transition-opacity hover:opacity-70 ` +
  `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F] focus-visible:ring-offset-2 rounded-sm`;

const Dot = () => (
  <span aria-hidden="true" className={`inline-block h-[5px] w-[5px] rounded-full ${DOT}`} />
);

const NavItem: React.FC<{ item: NavEntry }> = ({ item }) => {
  const { t } = useLocale();
  return (
    <NavLink to={item.to} className={navLinkClass}>
      {t(item.labelKey)}
    </NavLink>
  );
};

const NavGroup: React.FC<{ items: NavEntry[] }> = ({ items }) => (
  <ul className="flex items-center gap-4 xl:gap-5 2xl:gap-[25px]">
    {items.map((item, idx) => (
      <React.Fragment key={item.to}>
        <li>
          <NavItem item={item} />
        </li>
        {idx < items.length - 1 && (
          <li className="flex items-center">
            <Dot />
          </li>
        )}
      </React.Fragment>
    ))}
  </ul>
);

const LanguageSwitcher: React.FC<{ className?: string }> = ({ className }) => {
  const { locale, toggleLocale, t } = useLocale();
  return (
    <button
      type="button"
      onClick={toggleLocale}
      aria-label={t("header.aria_language")}
      className={
        `inline-flex items-center gap-3 ${ANTHRAZIT} font-display uppercase ` +
        `tracking-[0.15em] text-[14px] xl:text-[16px] 2xl:text-[18px] ` +
        `hover:opacity-70 transition-opacity ` +
        `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F] rounded-sm ` +
        (className ?? "")
      }
    >
      <span>{locale}</span>
      <img src={dropdownArrow} alt="" width={17} height={8} aria-hidden="true" loading="lazy" decoding="async" />
    </button>
  );
};

const MobileMenuItem: React.FC<{ item: NavEntry; onNavigate: () => void }> = ({ item, onNavigate }) => {
  const { t } = useLocale();
  return (
    <NavLink to={item.to} onClick={onNavigate} className={navLinkClass + " text-[18px]"}>
      {t(item.labelKey)}
    </NavLink>
  );
};

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 w-full bg-[#F2F2F2]/90 backdrop-blur-sm"
      aria-label="Hauptkopf"
    >
      <div className="relative mx-auto flex h-[72px] lg:h-[88px] 2xl:h-[103px] w-full max-w-[1310px] items-center px-4 sm:px-6 lg:px-8">
        {/* Desktop links: phone/mail */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-5">
          <a
            href="tel:+436645380244"
            aria-label="Anrufen"
            className="inline-flex h-10 w-10 items-center justify-center rounded-sm hover:opacity-70 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F]"
          >
            <img src={phoneIcon} alt="" width={26} height={26} aria-hidden="true" className="h-[26px] w-[26px]" loading="lazy" decoding="async" />
          </a>
          <a
            href="mailto:info@lerch-suites.at"
            aria-label="E-Mail schreiben"
            className="inline-flex h-10 w-10 items-center justify-center rounded-sm hover:opacity-70 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F]"
          >
            <img src={mailIcon} alt="" width={34} height={34} aria-hidden="true" loading="lazy" decoding="async" />
          </a>
        </div>

        {/* Mobile/Tablet: Logo links */}
        <div className="flex lg:hidden items-center">
          <Link
            to="/"
            aria-label="Startseite"
            className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F] rounded-sm"
          >
            <img
              src={bildmarke}
              alt="Lerch Suites"
              width={83}
              height={75}
              decoding="async"
              className="h-12 w-auto" loading="lazy"
            />
          </Link>
        </div>

        {/* Desktop: Nav + Logo */}
        <div className="hidden lg:flex flex-1 items-center justify-center gap-[90px]">
          <nav aria-label="Hauptnavigation links">
            <NavGroup items={LEFT_NAV} />
          </nav>

          <Link
            to="/"
            aria-label="Startseite"
            className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F] rounded-sm"
          >
            <img
              src={bildmarke}
              alt="Lerch Suites"
              width={83}
              height={75}
              decoding="async"
              className="h-12 w-auto lg:h-14 2xl:h-[75px]" loading="lazy"
            />
          </Link>

          <nav aria-label="Hauptnavigation rechts">
            <NavGroup items={RIGHT_NAV} />
          </nav>
        </div>

        <div className="hidden lg:flex items-center">
          <LanguageSwitcher />
        </div>

        {/* Spacer + Burger right on mobile */}
        <div className="flex-1 lg:hidden" />
        <div className="flex lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Menü öffnen"
                aria-expanded={open}
                className="inline-flex h-12 w-12 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F]"
              >
                <MenuIcon className={`h-8 w-8 ${ANTHRAZIT}`} aria-hidden="true" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85vw] max-w-sm bg-[#F2F2F2] border-none p-0 [&>button]:hidden"
            >
              {/* Header row mit Close */}
              <div className="flex items-center justify-end px-5 pt-4">
                <button
                  type="button"
                  aria-label="Menü schließen"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-12 w-12 items-center justify-center outline-none focus:outline-none"
                >
                  <XIcon className={`h-8 w-8 ${ANTHRAZIT}`} aria-hidden="true" strokeWidth={1.5} />
                </button>
              </div>

              <nav aria-label="Mobile Navigation" className="px-6 pb-6 mt-12 flex flex-col gap-6">
                {[...LEFT_NAV, ...RIGHT_NAV].map((item) => (
                  <MobileMenuItem key={item.to} item={item} onNavigate={() => setOpen(false)} />
                ))}
                <div className="mt-6 pt-6 border-t-2 border-[#6C184F] flex items-center gap-5">
                  <a href="tel:+436645380244" aria-label="Anrufen" className="inline-flex">
                    <img src={phoneIcon} alt="" width={24} height={24} aria-hidden="true" loading="lazy" decoding="async" />
                  </a>
                  <a href="mailto:info@lerch-suites.at" aria-label="E-Mail schreiben" className="inline-flex">
                    <img src={mailIcon} alt="" width={24} height={24} aria-hidden="true" loading="lazy" decoding="async" />
                  </a>
                  <LanguageSwitcher className="ml-auto" />
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
